import StorylineServerProxy  from "../storyline-proxy/storyline-proxy";
import { useCallback, useEffect, useRef, useState } from "react"
import { Box, Button, CircularProgress, Modal, Paper, Typography } from '@mui/material';

import ScrapReportDetails from "./scrap-report-details";
import { LinkDetails } from "./link-details";
import Paginator from "../GeneralComponents/paginator";
import ScrapReport from "./scrap-report";


export default function ScrapReportsPage({ failedReports }: any){


    let [infoOpen, setInfoOpen] = useState(false);
    let [selectedInfoScrapReport, setSelectedInfoScrapReport] = useState(null as any);

    let [linkOpen, setLinkOpen] = useState(false);
    let [selectedScrapReportAndLink, setSelectedScrapReportAndLink] = useState({ link: "", scrapReport: null as any});
    const [linksInStoryline, setLinksInStoryline] = useState([] as string[]);

    const markLinkAsIncluded = useCallback((link: string) => {
        setLinksInStoryline(current => current.includes(link) ? current : [...current, link]);
    }, []);

    function handleCloseInfo(){
        setInfoOpen(false);
    }

    function handleShowInfo(scrapReport: any){
        setSelectedInfoScrapReport(scrapReport);
        setInfoOpen(true);
    }

    function handleLinkClicked(link: string, scrapReport: any){
        setSelectedScrapReportAndLink({ link: link, scrapReport: scrapReport });
        setLinkOpen(true);
    }

    function handleCloseLink(){
        setLinkOpen(false);
    }

    return(
        <>
            <ScrapReportsList
                failedReports={failedReports}
                handleInfoClick={handleShowInfo}
                handleOpenClick={handleLinkClicked}
                linksInStoryline={linksInStoryline}
                markLinkAsIncluded={markLinkAsIncluded}
            />
            <Modal sx={{ left: 100, top: 100, width:9/10 }} open={infoOpen} onClose={handleCloseInfo}>
                { selectedInfoScrapReport ? (
                    <>
                        <Paper elevation = {4} sx={{ padding: 5, paddingBottom: 2}}>
                            <ScrapReportDetails scrapReport={selectedInfoScrapReport} />
                            <Box sx={{ display: 'flex', alignItems: 'flex-end' }}>
                                <Button sx={{ marginLeft: "auto" }} onClick={handleCloseInfo}>Close</Button>
                            </Box>
                        </Paper>
                    </>
                ):(<></>)}
            </Modal>
            <Modal sx={{ left: 100, top: 10, width:9/10 }} open={linkOpen} onClose={handleCloseLink}>
                <Paper sx={{ padding: 5, paddingBottom: 2 }} elevation={4}>
                    <LinkDetails scrapReport={selectedScrapReportAndLink.scrapReport} link={selectedScrapReportAndLink.link} handleCloseClicked={handleCloseLink} markLinkAsIncluded={markLinkAsIncluded} />
                    <Box sx={{ display:'flex', alignItems:'flex-end'}}  >
                        <Button sx={{ marginLeft: "auto" }} onClick={handleCloseLink}>Close</Button>
                    </Box>
                </Paper>
            </Modal>

      </>
    )
}



export function ScrapReportsList({
    failedReports,
    handleInfoClick = (scrapReport: any) => {},
    handleOpenClick = (link: string, scrapReport: any) => {},
    linksInStoryline,
    markLinkAsIncluded
}: any){

    let [scrapReports, setScrapReports] = useState([] as any[]);
    let [scrapReportsCount, setScrapReportsCount] = useState(0);

    type ReportLinkStatus = {
        signature: string,
        includedLinks: string[],
        ready: boolean
    };
    const [reportLinkStatuses, setReportLinkStatuses] = useState({} as Record<string, ReportLinkStatus>);

    let [analysIsRunning, setAnalysIsRunning] = useState(false);

    let [pageNumber, setPageNumber] = useState(0);
    const [pageSize, setPageSize] = useState(10);

    let [loading, setLoading] = useState(false);
    const [, setRefreshing] = useState(false);
    let [errorMessage, setErrorMessage] = useState("");

    let [lastOpenedScraper, setLastOpenedScraper] = useState(null);

    const pageNumberRef = useRef(pageNumber);
    const pageSizeRef = useRef(pageSize);
    const requestIdRef = useRef(0);
    const skipNextForegroundLoadRef = useRef(false);
    const reportLinkStatusesRef = useRef(reportLinkStatuses);

    pageNumberRef.current = pageNumber;
    pageSizeRef.current = pageSize;

    function openScraperTargetInChildWindow(scraper: any){
        setLastOpenedScraper(scraper);
        window.open(scraper.url, "child");
    }

    function updateReportLinkStatuses(statuses: Record<string, ReportLinkStatus>){
        reportLinkStatusesRef.current = statuses;
        setReportLinkStatuses(statuses);
    }

    function reportLinks(scrapReport: any): string[]{
        return Array.from(new Set((scrapReport.bufferedNew || []).filter((link: any) => typeof(link) == "string")));
    }

    async function validateReportLinks(scrapReports: any[], requestId: number){
        const nextStatuses = {} as Record<string, ReportLinkStatus>;
        const validations = [] as Promise<void>[];

        for(const scrapReport of scrapReports){
            const links = reportLinks(scrapReport);
            const signature = links.join("\n");
            const cachedStatus = reportLinkStatusesRef.current[scrapReport._id];

            if(cachedStatus && cachedStatus.signature == signature){
                nextStatuses[scrapReport._id] = cachedStatus;
                continue;
            }

            nextStatuses[scrapReport._id] = {
                signature: signature,
                includedLinks: [],
                ready: links.length == 0
            };

            if(links.length == 0){
                continue;
            }

            validations.push((async () => {
                const requests = [] as Promise<any>[];
                for(let index = 0; index < links.length; index += 100){
                    const request = StorylineServerProxy.checkLinksUnicity(links.slice(index, index + 100));
                    if(request){
                        requests.push(request);
                    }
                }

                let includedLinks = [] as string[];
                try{
                    const results = await Promise.all(requests);
                    includedLinks = Array.from(new Set(results.flatMap(result => result.links)));
                }
                catch(error){
                    console.error("Unable to check whether a scrap report's links are already in Storyline", error);
                }
                finally{
                    if(requestId == requestIdRef.current){
                        const currentStatus = reportLinkStatusesRef.current[scrapReport._id];
                        if(currentStatus && currentStatus.signature == signature){
                            updateReportLinkStatuses({
                                ...reportLinkStatusesRef.current,
                                [scrapReport._id]: {
                                    signature: signature,
                                    includedLinks: includedLinks,
                                    ready: true
                                }
                            });
                        }
                    }
                }
            })());
        }

        updateReportLinkStatuses(nextStatuses);
        await Promise.all(validations);
    }

    async function reportsLoaded(scrapReports: any[], requestId: number){
        if(requestId != requestIdRef.current){
            return;
        }
        setScrapReports(scrapReports);
        await validateReportLinks(scrapReports, requestId);
    }

    function getReportsCount(){
        return (failedReports ? StorylineServerProxy.getFailedScrapReportsCount() : StorylineServerProxy.getScrapReportsCount()) as Promise<any>;
    }

    function getReports(size: number, page: number){
        return (failedReports ? StorylineServerProxy.getFailedScrapReports(size, page) : StorylineServerProxy.getScrapReports(size, page)) as Promise<any>;
    }

    async function loadList(background = false, deletedReportId: string | null = null){
        const requestId = ++requestIdRef.current;
        const currentPage = pageNumberRef.current;
        const currentPageSize = pageSizeRef.current;

        if(background){
            setRefreshing(true);
        }
        else{
            setScrapReports([]);
            setLoading(true);
            updateReportLinkStatuses({});
        }
        setErrorMessage("");

        try{
            const [countResult, currentPageReports] = await Promise.all([
                getReportsCount(),
                getReports(currentPageSize, currentPage)
            ]);
            if(requestId != requestIdRef.current){
                return;
            }

            const count = countResult.count;
            const lastPage = Math.max(0, Math.ceil(count / currentPageSize) - 1);
            const targetPage = Math.min(currentPage, lastPage);
            const reports: any[] = targetPage == currentPage
                ? currentPageReports
                : await getReports(currentPageSize, targetPage);

            if(requestId != requestIdRef.current){
                return;
            }
            setScrapReportsCount(count);
            if(targetPage != currentPage){
                skipNextForegroundLoadRef.current = true;
                pageNumberRef.current = targetPage;
                setPageNumber(targetPage);
            }
            await reportsLoaded(reports, requestId);
            if(deletedReportId && reports.some(report => report._id == deletedReportId)){
                setErrorMessage("Unable to delete the scrap report. It has been restored.");
            }
        }
        catch(error){
            if(requestId == requestIdRef.current){
                console.error("Unable to load scrap reports", error);
                setErrorMessage("Unable to refresh the scrap reports. Please try again.");
            }
        }
        finally{
            if(requestId == requestIdRef.current){
                setLoading(false);
                setRefreshing(false);
            }
        }
    }

    useEffect(() => {
        if(skipNextForegroundLoadRef.current){
            skipNextForegroundLoadRef.current = false;
            return;
        }
        loadList(false);
    }, [pageNumber, pageSize, failedReports]);

    function handleDeleteClick(scrapReport: any){
        // NOTE: no confirmation, as this was slowing down the processing
        const removedIndex = scrapReports.findIndex(report => report._id == scrapReport._id);
        setScrapReports(current => current.filter(report => report._id != scrapReport._id));
        setErrorMessage("");

        const deleteRequest = StorylineServerProxy.markScrapReportAsDeleted(scrapReport._id);
        if(!deleteRequest){
            setScrapReports(current => {
                const restored = [...current];
                restored.splice(Math.max(0, removedIndex), 0, scrapReport);
                return restored;
            });
            setErrorMessage("Unable to delete the scrap report. It has been restored.");
            return;
        }

        deleteRequest
            .then(response => {
                if(response === undefined){
                    throw new Error("The server did not confirm the deletion");
                }
                return loadList(true, scrapReport._id);
            })
            .catch(error => {
                console.error("Unable to delete scrap report", error);
                setScrapReports(current => {
                    if(current.some(report => report._id == scrapReport._id)){
                        return current;
                    }
                    const restored = [...current];
                    restored.splice(Math.max(0, removedIndex), 0, scrapReport);
                    return restored;
                });
                setErrorMessage("Unable to delete the scrap report. It has been restored.");
            });
    }

    function handleAnalysisClick(scrapReport: any){
        setAnalysIsRunning(true)
        StorylineServerProxy.analyseScrapReport(scrapReport._id).then(() => {
            setAnalysIsRunning(false);
            loadList(true);
        });
    }



    function handleDeleteAllClicked(){
        StorylineServerProxy.markAllFailedAsDeleted().then(() => { loadList(true) });
    }


    return (
        <>
            <input type="hidden" id="lastOpenedScraper" value={JSON.stringify(lastOpenedScraper)}></input>
            <Paginator pageNumber={pageNumber} pageSize={pageSize} itemsCount={scrapReportsCount} itemType={"scrap reports"} setPageNumber={setPageNumber} setPageSize={setPageSize} />
            <br/>
            { failedReports?<><Button onClick={handleDeleteAllClicked} >Delete all</Button><br /><br /></>:<></> }
            { errorMessage?<Typography color="error">{errorMessage}</Typography>:<></> }
            {
                loading?<CircularProgress />:<></>
            }
            {
                scrapReports.map(scrapReport => {
                    const linkStatus = reportLinkStatuses[scrapReport._id];
                    const includedLinks = Array.from(new Set([
                        ...linksInStoryline,
                        ...(linkStatus?.includedLinks || [])
                    ]));
                    return <ScrapReport key={scrapReport._id} scrapReport={scrapReport} handleOpenChildClicked={openScraperTargetInChildWindow} handleAnalysisClick={handleAnalysisClick} handleInfoClick={handleInfoClick} handleDeleteClick={handleDeleteClick} analysisIsRunning={analysIsRunning} linksInStorylineReady={linkStatus?.ready || false} linksInStoryline={includedLinks} handleLinkClicked={handleOpenClick} markLinkAsIncluded={markLinkAsIncluded}/>
                })
            }
        </>
    )
}
